import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Download, Copy, Edit3, Trash2, Plus, Wallet,
  Printer, Send, X, MessageCircle, Mail,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";

function PaymentModal({ open, onClose, onSaved, invoice }) {
  const [f, setF] = useState({
    amount: invoice?.balance_due || 0,
    date: new Date().toISOString().slice(0, 10),
    method: "Bank", reference: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF((s) => ({ ...s, amount: invoice?.balance_due || 0, date: new Date().toISOString().slice(0, 10) }));
  }, [open, invoice]);
  if (!open) return null;
  const submit = async () => {
    if (!f.amount || f.amount <= 0) { toast.error("Amount must be > 0"); return; }
    setSaving(true);
    try {
      await api.post(`/invoices/${invoice.id}/payments`, f);
      toast.success("Payment recorded");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="payment-modal">
      <div className="bg-white rounded-lg w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold">Record Payment</h2>
            <p className="text-xs text-[#64748B] mt-0.5">Balance due: <span className="font-mono-data font-semibold">{fmtMoney(invoice?.balance_due, invoice?.currency)}</span></p>
          </div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="label-field">Amount *</label>
            <input data-testid="pay-amount" type="number" step="0.01" className="input-clean font-mono-data" value={f.amount} onChange={(e) => setF({ ...f, amount: parseFloat(e.target.value) || 0 })}/></div>
          <div><label className="label-field">Date *</label>
            <input data-testid="pay-date" type="date" className="input-clean" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })}/></div>
          <div><label className="label-field">Method</label>
            <select data-testid="pay-method" className="input-clean" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>
              <option>Cash</option><option>Bank</option><option>UPI</option><option>Card</option><option>Cheque</option>
            </select></div>
          <div><label className="label-field">Reference</label>
            <input className="input-clean" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="Txn ID / Cheque #"/></div>
          <div className="md:col-span-2"><label className="label-field">Notes</label>
            <textarea className="input-clean" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}/></div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
          <button data-testid="pay-save-btn" onClick={submit} disabled={saving} className="btn-primary w-full sm:w-auto justify-center">
            {saving ? "Saving…" : "Record Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const { active } = useCompany();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
    } catch (e) {
      toast.error("Failed to load invoice");
      navigate("/app/invoices");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.number.replace(/\//g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF generation failed"); }
  };

  const printPdf = async () => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("PDF generation failed"); }
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/invoices/${id}/duplicate`);
      toast.success(`Created ${data.number}`);
      navigate(`/app/invoices/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete invoice ${inv.number}?`)) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Deleted");
      navigate("/app/invoices");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removePayment = async (pid) => {
    if (!window.confirm("Delete this payment?")) return;
    try {
      await api.delete(`/invoices/${id}/payments/${pid}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (status) => {
    try {
      await api.put(`/invoices/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading || !inv) return <div className="text-center py-12 text-[#64748B]">Loading…</div>;
  const cur = inv.currency;
  const canEdit = inv.paid_amount === 0 && inv.status !== "paid";

  return (
    <div className="space-y-4" data-testid="invoice-detail-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate(-1)} className="btn-ghost shrink-0" aria-label="Back"><ArrowLeft size={16}/></button>
          <div className="min-w-0">
            <div className="overline mb-1">
              {inv.direction === "purchase" ? "Purchase Invoice" : (inv.invoice_type === "bill_of_supply" ? "Bill of Supply" : "Tax Invoice")}
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-[#0F172A] font-mono-data truncate">{inv.number}</h1>
          </div>
          <span className={`chip chip-${inv.status === "paid" ? "success" : inv.status === "draft" ? "neutral" : inv.status === "cancelled" ? "danger" : "warn"} text-xs shrink-0`}>
            {inv.status}
          </span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="inv-download-pdf" onClick={downloadPdf} className="btn-secondary">
            <Download size={14}/> PDF
          </button>
          <button data-testid="inv-print-pdf" onClick={printPdf} className="btn-ghost">
            <Printer size={14}/> Preview
          </button>
          <button data-testid="inv-duplicate" onClick={duplicate} className="btn-ghost">
            <Copy size={14}/> Duplicate
          </button>
          {canEdit && (
            <Link to={`/app/invoices/${inv.id}/edit`} data-testid="inv-edit" className="btn-ghost">
              <Edit3 size={14}/> Edit
            </Link>
          )}
          {inv.balance_due > 0 && inv.status !== "cancelled" && (
            <button data-testid="inv-record-payment" onClick={() => setPayOpen(true)} className="btn-primary">
              <Wallet size={14}/> Record Payment
            </button>
          )}
          {inv.status === "draft" && (
            <button data-testid="inv-mark-sent" onClick={() => updateStatus("sent")} className="btn-ghost">
              <Send size={14}/> Mark Sent
            </button>
          )}
          <button
            data-testid="inv-whatsapp"
            onClick={() => {
              const phone = inv.party_snapshot?.phone;
              if (!phone) { toast.error("Customer has no phone number"); return; }
              const num = phone.replace(/[^\d]/g, "");
              const msg = `Hi ${inv.party_snapshot?.name || ""}, please find your invoice ${inv.number} for ${fmtMoney(inv.grand_total, inv.currency)}. Thank you!`;
              window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
            }}
            className="btn-whatsapp"
          >
            <MessageCircle size={14}/> WhatsApp
          </button>
          <button
            data-testid="inv-email"
            onClick={async () => {
              const to = inv.party_snapshot?.email;
              if (!to) {
                toast.error("Customer has no email on file. Add it on the party record.");
                return;
              }
              if (!window.confirm(`Send invoice ${inv.number} to ${to}?`)) return;
              try {
                await api.post(`/invoices/${inv.id}/send-email`, {});
                toast.success(`Invoice emailed to ${to}`);
                load?.();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed to send email");
              }
            }}
            className="btn-ghost border border-[#E2E8F0]"
          >
            <Mail size={14}/> Email
          </button>
          <button
            data-testid="inv-stripe-link"
            onClick={async () => {
              try {
                const { data } = await api.post(`/invoices/${inv.id}/stripe-pay-link`);
                if (data?.url) {
                  await navigator.clipboard.writeText(data.url).catch(() => {});
                  toast.success("Stripe pay link created — copied to clipboard");
                  window.open(data.url, "_blank", "noopener");
                  load?.();
                }
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed to create Stripe pay link");
              }
            }}
            className="btn-ghost border border-[#E2E8F0]"
            title="Generate a Stripe Checkout URL — auto-embedded in next WhatsApp / Email"
          >
            <Wallet size={14}/> Pay Link
          </button>
          {(inv.stripe_session_id && (inv.status === "paid" || (inv.paid_amount || 0) > 0)) && (
            <button
              data-testid="inv-refund"
              onClick={async () => {
                const raw = window.prompt(
                  `Refund amount in ${inv.currency || "INR"} (blank = full refund of ${(inv.paid_amount || 0) - (inv.refunded_amount || 0)}):`,
                  ""
                );
                if (raw === null) return;
                const amount = raw.trim() ? parseFloat(raw.trim()) : null;
                const reason = window.prompt("Reason (optional):", "") || "";
                try {
                  const { data } = await api.post(`/invoices/${inv.id}/refund`, { amount, reason });
                  toast.success(`Refund ${data.credit_note_number} issued — ${data.currency} ${data.amount}`);
                  load?.();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Refund failed");
                }
              }}
              className="btn-ghost border border-[#DC2626] text-[#DC2626]"
              title="Issue Stripe refund + auto-generate credit note"
            >
              <X size={14}/> Refund
            </button>
          )}
          <button data-testid="inv-delete" onClick={remove} className="btn-ghost text-[#DC2626]">
            <Trash2 size={14}/>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Party */}
        <div className="card-surface p-5">
          <div className="overline mb-2">{inv.direction === "purchase" ? "Supplier" : "Bill to"}</div>
          <div className="font-semibold text-[#0F172A] mb-1">{inv.party_snapshot?.name}</div>
          {inv.party_snapshot?.gstin && <div className="text-xs font-mono-data text-[#64748B]">GSTIN: {inv.party_snapshot.gstin}</div>}
          {inv.party_snapshot?.billing_address && <div className="text-xs text-[#64748B] mt-1 whitespace-pre-wrap">{inv.party_snapshot.billing_address}</div>}
          {inv.party_snapshot?.email && <div className="text-xs text-[#64748B] mt-1">{inv.party_snapshot.email}</div>}
        </div>
        {/* Meta */}
        <div className="card-surface p-5 space-y-2">
          <div className="overline mb-2">Details</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="text-[#64748B]">Date</div><div className="font-mono-data font-medium">{inv.invoice_date}</div>
            {inv.due_date && <><div className="text-[#64748B]">Due</div><div className="font-mono-data font-medium">{inv.due_date}</div></>}
            {inv.place_of_supply && <><div className="text-[#64748B]">Place of Supply</div><div className="font-mono-data font-medium">{inv.place_of_supply}</div></>}
            {inv.reference && <><div className="text-[#64748B]">Vendor Ref</div><div className="font-mono-data font-medium">{inv.reference}</div></>}
            <div className="text-[#64748B]">Currency</div><div className="font-mono-data font-medium">{inv.currency} @ {inv.exchange_rate}</div>
            {inv.source_so_id && (
              <><div className="text-[#64748B]">Source SO</div>
                <Link to="/app/sales-orders" className="font-mono-data font-medium text-[#2563EB]" data-testid="inv-source-so">Sales Order →</Link></>
            )}
            {inv.source_quotation_id && (
              <><div className="text-[#64748B]">Source Quote</div>
                <Link to="/app/quotations" className="font-mono-data font-medium text-[#2563EB]" data-testid="inv-source-quote">Quotation →</Link></>
            )}
          </div>
        </div>
        {/* Amounts */}
        <div className="card-surface p-5">
          <div className="overline mb-2">Amounts</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-[#64748B]">Grand Total</span><span className="font-mono-data font-bold">{fmtMoney(inv.grand_total, cur)}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Paid</span><span className="font-mono-data text-[#16A34A]">{fmtMoney(inv.paid_amount, cur)}</span></div>
            <div className="flex justify-between border-t border-[#E2E8F0] pt-2 mt-2">
              <span className="text-[#64748B] font-semibold">Balance Due</span>
              <span className={`font-mono-data font-bold ${inv.balance_due > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>
                {fmtMoney(inv.balance_due, cur)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Lines */}
      <div className="card-surface p-4">
        <h3 className="font-semibold mb-3">Line Items</h3>
        <table className="table-clean">
          <thead>
            <tr>
              <th>#</th><th>Item</th><th>HSN</th><th className="text-right">Qty</th>
              <th className="text-right">Rate</th><th className="text-right">Taxable</th>
              {inv.cgst_total > 0 && <><th className="text-right">CGST</th><th className="text-right">SGST</th></>}
              {inv.igst_total > 0 && <th className="text-right">IGST</th>}
              {inv.vat_total > 0 && <th className="text-right">VAT</th>}
              {inv.sales_tax_total > 0 && <th className="text-right">Tax</th>}
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((ln, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td className="font-medium">{ln.name}</td>
                <td className="font-mono-data text-xs">{ln.hsn || "-"}</td>
                <td className="text-right font-mono-data">{ln.qty}</td>
                <td className="text-right font-mono-data">{fmtMoney(ln.unit_price, cur)}</td>
                <td className="text-right font-mono-data">{fmtMoney(ln.taxable, cur)}</td>
                {inv.cgst_total > 0 && <><td className="text-right font-mono-data">{fmtMoney(ln.cgst, cur)}</td><td className="text-right font-mono-data">{fmtMoney(ln.sgst, cur)}</td></>}
                {inv.igst_total > 0 && <td className="text-right font-mono-data">{fmtMoney(ln.igst, cur)}</td>}
                {inv.vat_total > 0 && <td className="text-right font-mono-data">{fmtMoney(ln.vat, cur)}</td>}
                {inv.sales_tax_total > 0 && <td className="text-right font-mono-data">{fmtMoney(ln.sales_tax, cur)}</td>}
                <td className="text-right font-mono-data font-semibold">{fmtMoney(ln.total, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payments */}
      {inv.payments && inv.payments.length > 0 && (
        <div className="card-surface p-4" data-testid="payments-history">
          <h3 className="font-semibold mb-3">Payment History</h3>
          <table className="table-clean">
            <thead><tr><th>Date</th><th>Method</th><th>Reference</th><th className="text-right">Amount</th><th></th></tr></thead>
            <tbody>
              {inv.payments.map((p) => (
                <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                  <td className="font-mono-data">{p.date}</td>
                  <td>{p.method}</td>
                  <td className="text-xs font-mono-data">{p.reference || "-"}</td>
                  <td className="text-right font-mono-data font-semibold text-[#16A34A]">{fmtMoney(p.amount, cur)}</td>
                  <td><button onClick={() => removePayment(p.id)} className="text-[#DC2626]"><Trash2 size={12}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inv.notes && (
        <div className="card-surface p-5">
          <div className="overline mb-2">Notes / Terms</div>
          <p className="text-sm whitespace-pre-wrap">{inv.notes}</p>
        </div>
      )}

      <PaymentModal open={payOpen} onClose={() => setPayOpen(false)} onSaved={load} invoice={inv} />
    </div>
  );
}
